import React, { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import type CampaignContact from "@/model/interface/CampaignContact";
import { DialerQueueGroup } from "@/enum/DialerQueueGroup";
import { useGetDialerQueueQuery } from "@/store/rest_api";
import { Button } from "@/components/ui/button";
import { QueuedContactItem } from "@/components/dialer/components/QueuedContactItem";
import { CompletedCallItem } from "@/components/dialer/components/CompletedCallItem";
import { HeldLeadItem } from "@/components/dialer/power-dialer/HeldLeadItem";
import { formatClockTime } from "@/util/date";

const PAGE_SIZE = 25;
const MAX_PAGE_SIZE = 100;
const REFRESH_INTERVAL_MS = 15_000;

interface QueueGroupSectionProps {
  campaignId: string;
  group: DialerQueueGroup;
  label: string;
  count: number;
  defaultOpen?: boolean;
}

function detailFor(group: DialerQueueGroup, lead: CampaignContact): string | undefined {
  if (group === DialerQueueGroup.RETRY_LATER && lead.recallAt) {
    return `Callback at ${formatClockTime(lead.recallAt)}`;
  }
  if (group === DialerQueueGroup.RETRY_LATER && lead.nextRetryAt) {
    return `Retry at ${formatClockTime(lead.nextRetryAt)}`;
  }
  if (group === DialerQueueGroup.SKIPPED) {
    return lead.exitReason;
  }
  return undefined;
}

export function QueueGroupSection({ campaignId, group, label, count, defaultOpen = false }: QueueGroupSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const { data } = useGetDialerQueueQuery(
    { campaignId, group, page: 0, size: pageSize },
    { skip: !isOpen || count === 0, pollingInterval: REFRESH_INTERVAL_MS },
  );
  const leads = data?.items ?? [];
  const hasMore = data !== undefined && data.totalElements > leads.length && pageSize < MAX_PAGE_SIZE;

  return (
    <div>
      <button
        type="button"
        className="mb-1.5 flex w-full items-center gap-1.5 px-1 text-sm font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground"
        onClick={() => setIsOpen((open) => !open)}
      >
        {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        <span>{label}</span>
        <span className="ml-auto tabular-nums">{count}</span>
      </button>
      {isOpen && count === 0 && <p className="px-1 py-1.5 text-sm text-muted-foreground">None</p>}
      {isOpen &&
        leads.map((lead) => {
          if (group === DialerQueueGroup.DONE) {
            return <CompletedCallItem key={lead.id} lead={lead} />;
          }
          if (group === DialerQueueGroup.OUTSIDE_CALLING_HOURS || group === DialerQueueGroup.DAILY_CAP) {
            return <HeldLeadItem key={lead.id} lead={lead} />;
          }
          return <QueuedContactItem key={lead.id} contact={lead.contact} detail={detailFor(group, lead)} />;
        })}
      {isOpen && hasMore && (
        <Button
          variant="ghost"
          size="sm"
          className="mt-1 w-full text-sm"
          onClick={() => setPageSize((size) => Math.min(size + PAGE_SIZE, MAX_PAGE_SIZE))}
        >
          Show more
        </Button>
      )}
    </div>
  );
}
